/**
 * Period totals and the one comparison the app makes (SPEC.md §8).
 *
 * There is no analytics section, deliberately. This is the whole of "looking
 * back": what you spent, and whether you are ahead of or behind last period at
 * the same point in it.
 */

import { type Cents, ZERO, cents, sumCents } from "./money";
import { type Period, dayIndexInPeriod, previousPeriod } from "./period";

export interface TotalledExpense {
  readonly categoryId: string;
  readonly occurredAt: string;
  readonly amountCents: Cents;
}

export interface PeriodTotals {
  readonly total: Cents;
  readonly byCategory: ReadonlyMap<string, Cents>;
  readonly count: number;
}

/** Spend for a period, overall and per category. One pass. */
export function periodTotals(
  expenses: readonly TotalledExpense[],
  period: Period,
): PeriodTotals {
  const from = period.start.getTime();
  const to = period.end.getTime();
  const byCategory = new Map<string, Cents>();
  const amounts: Cents[] = [];

  for (const e of expenses) {
    const t = new Date(e.occurredAt).getTime();
    if (t < from || t >= to) continue;

    amounts.push(e.amountCents);
    const running = byCategory.get(e.categoryId) ?? ZERO;
    byCategory.set(e.categoryId, cents(running + e.amountCents));
  }

  return { total: sumCents(amounts), byCategory, count: amounts.length };
}

/**
 * Spend from the period's start up to and including `throughDayIndex`.
 * The like-for-like measure the comparison depends on.
 */
export function spendThroughDay(
  expenses: readonly TotalledExpense[],
  period: Period,
  throughDayIndex: number,
  timeZone: string,
): Cents {
  const from = period.start.getTime();
  const to = period.end.getTime();
  const amounts: Cents[] = [];

  for (const e of expenses) {
    const when = new Date(e.occurredAt);
    const t = when.getTime();
    if (t < from || t >= to) continue;
    if (dayIndexInPeriod(when, period, timeZone) <= throughDayIndex) amounts.push(e.amountCents);
  }
  return sumCents(amounts);
}

export interface Comparison {
  /** Positive = spending less than last period at this point. */
  readonly deltaCents: Cents;
  readonly thisPeriod: Cents;
  readonly lastPeriod: Cents;
  readonly dayIndex: number;
  /** False when there is no prior data worth comparing against. */
  readonly hasBasis: boolean;
}

/**
 * Compare this period against the last one **at the same day index**.
 *
 * Day *n* versus day *n* — not total versus total. Comparing a partial month
 * against a complete one would tell every user, every time, that they are doing
 * brilliantly until the 28th. That is worse than saying nothing.
 */
export function comparison(
  expenses: readonly TotalledExpense[],
  period: Period,
  now: Date,
  cycleStartDay: number,
  timeZone: string,
): Comparison {
  const dayIndex = Math.max(0, dayIndexInPeriod(now, period, timeZone));
  const prior = previousPeriod(period, cycleStartDay, timeZone);

  const thisPeriod = spendThroughDay(expenses, period, dayIndex, timeZone);
  const lastPeriod = spendThroughDay(expenses, prior, dayIndex, timeZone);

  const priorHasAny = expenses.some((e) => {
    const t = new Date(e.occurredAt).getTime();
    return t >= prior.start.getTime() && t < prior.end.getTime();
  });

  return {
    deltaCents: cents(lastPeriod - thisPeriod),
    thisPeriod,
    lastPeriod,
    dayIndex,
    hasBasis: priorHasAny,
  };
}

/** Order categories by spend, descending — the order Ledger lists them in. */
export function rankCategories(totals: PeriodTotals): { categoryId: string; spent: Cents }[] {
  return [...totals.byCategory.entries()]
    .map(([categoryId, spent]) => ({ categoryId, spent }))
    .sort((a, b) => b.spent - a.spent);
}

/**
 * Suggested daily allowance for the rest of the period.
 * Zero once the budget is exhausted — this never suggests a negative allowance.
 */
export function dailyAllowance(remaining: Cents, daysLeft: number): Cents {
  if (daysLeft <= 0 || remaining <= 0) return ZERO;
  return cents(Math.floor(remaining / daysLeft));
}
